import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classnames from "classnames";
import Moment from "react-moment";
import { connect } from "react-redux";
import { Button, Input } from "reactstrap";
import { deletePart, getPartsCatalog } from "../../../actions";
import { usePartRefreshQueue } from "../../../context/PartRefreshQueueContext";
import Datatable from "../../Datatable";
import Paginations from "../../Datatable/Paginations";
import Layouts from "../../Layouts";
import DeleteConfirmation from "../../Modals/DeleteConfirmation.js";
import "./partsStyle.css";

const mountTypeMeta = (value) => {
  const mount = String(value || "").trim();
  if (/surface\s*mount/i.test(mount)) {
    return { key: "smt", label: "Surface Mount" };
  }
  if (/through\s*hole|thru\s*hole/i.test(mount)) {
    return { key: "tht", label: "Through Hole" };
  }
  return { key: "", label: displayValue(mount) };
};

const isMissingDetail = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();
  return (
    !text ||
    text === "-" ||
    text === "–" ||
    text === "—" ||
    text === "n/a" ||
    text === "na" ||
    text === "unknown"
  );
};

const displayValue = (value) => {
  if (isMissingDetail(value)) {
    return "N/A";
  }
  return String(value).trim();
};

const datasheetHref = (value) => {
  let url = String(value || "").trim();
  if (!url || isMissingDetail(url)) {
    return "";
  }
  if (url.indexOf("//") === 0) {
    url = `https:${url}`;
  } else if (!/^https?:\/\//i.test(url) && /^[\w.-]+\.[a-z]{2,}\//i.test(url)) {
    url = `https://${url}`;
  }
  if (!/^https?:\/\//i.test(url)) {
    return "";
  }
  return url;
};

const applyCatalogPatches = (current, patches) => {
  if (!Array.isArray(patches) || !patches.length) {
    return current;
  }
  return current.map((row) => {
    const patch = patches.find(
      (item) => Number(item.partId) === Number(row.id) && item.catalogRow,
    );
    return patch ? { ...row, ...patch.catalogRow } : row;
  });
};

const PartsDetailRow = ({ label, children }) => (
  <>
    <span className="parts-details__label">{label}</span>
    <span className="parts-details__value">{children}</span>
  </>
);

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const Parts = (props) => {
  const { user, getPartsCatalog, deletePart } = props;
  const { enqueue, items, lastCompleted } = usePartRefreshQueue();
  const canDelete = user && user.user.role_id === 1;

  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [mountingType, setMountingType] = useState("");
  const [partStatus, setPartStatus] = useState("");
  const [countryOfOrigin, setCountryOfOrigin] = useState("");
  const [showEntry, setShowEntry] = useState(25);
  const [pageActive, setPageActive] = useState(1);
  const [parts, setParts] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deletePartData, setDeletePartData] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(searchInput.trim());
      setPageActive(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadPartsRef = useRef(null);
  const catalogAbortRef = useRef(null);

  const loadParts = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (catalogAbortRef.current) {
      catalogAbortRef.current.abort();
    }
    const controller = new AbortController();
    catalogAbortRef.current = controller;

    if (!silent) {
      setIsLoading(true);
    }
    try {
      const res = await getPartsCatalog(
        user.token,
        {
          page: pageActive,
          per_page: showEntry,
          search: searchText,
          mounting_type: mountingType,
          part_status: partStatus,
          country_of_origin: countryOfOrigin,
          sort: "part_number",
          dir: "asc",
        },
        controller.signal,
      );
      if (controller.signal.aborted) {
        return;
      }
      const payload = res && res.data ? res.data : {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      const meta = payload.meta || {};
      setParts(items);
      setTotalRecords(Number(meta.total) || items.length);
      if (meta.current_page && Number(meta.current_page) !== pageActive) {
        setPageActive(Number(meta.current_page));
      }
    } catch (error) {
      if (error && error.name === "AbortError") {
        return;
      }
    } finally {
      if (!controller.signal.aborted && !silent) {
        setIsLoading(false);
      }
    }
  }, [
    getPartsCatalog,
    user.token,
    pageActive,
    showEntry,
    searchText,
    mountingType,
    partStatus,
    countryOfOrigin,
  ]);

  loadPartsRef.current = loadParts;

  useEffect(() => {
    loadParts();
    return () => {
      if (catalogAbortRef.current) {
        catalogAbortRef.current.abort();
      }
    };
  }, [loadParts]);

  useEffect(() => {
    const rows = Array.isArray(lastCompleted?.rows)
      ? lastCompleted.rows
      : lastCompleted?.catalogRow
        ? [lastCompleted]
        : [];
    if (!rows.length) {
      return;
    }
    setParts((current) => applyCatalogPatches(current, rows));
    const missingCatalogRow = rows.some(
      (item) => item.partId && !item.catalogRow,
    );
    if (missingCatalogRow) {
      loadPartsRef.current({ silent: true });
    }
  }, [lastCompleted]);

  useEffect(() => {
    const patches = items.filter(
      (item) => item.status === "success" && item.catalogRow,
    );
    if (!patches.length) {
      return;
    }
    setParts((current) => applyCatalogPatches(current, patches));
  }, [items]);

  const queueStatusByPartId = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      map[item.partId] = item;
    });
    return map;
  }, [items]);

  const showDeleteConfirm = (row) => {
    setDeletePartData(row);
    setDeleteConfirm(true);
  };

  const deleteConfirmPopup = async (confirm) => {
    setDeleteConfirm(false);
    if (!confirm || !deletePartData) {
      setDeletePartData(null);
      return;
    }
    const res = await props.deletePart(user.token, { id: deletePartData.id });
    if (res && res.code >= 200 && res.code < 400) {
      const nextTotal = Math.max(0, totalRecords - 1);
      const lastPage = Math.max(1, Math.ceil(nextTotal / showEntry));
      if (pageActive > lastPage) {
        setPageActive(lastPage);
      } else {
        loadParts();
      }
    }
    setDeletePartData(null);
  };

  const columns = [
    {
      fieldName: "part_number",
      label: "MPN",
      customRow: (row) => <strong>{displayValue(row.part_number)}</strong>,
    },
    {
      fieldName: "manufacturer",
      label: "Manufacturer",
      customRow: (row) => displayValue(row.manufacturer),
    },
    {
      fieldName: "digikey_part_number",
      label: "DigiKey PN",
      customRow: (row) => (
        <span>
          {displayValue(row.digikey_part_number)}
          {row.part_detail_url ? (
            <a
              href={row.part_detail_url}
              target="_blank"
              rel="noopener noreferrer"
              className="parts-external-link"
              title="View on DigiKey"
            >
              <i className="fa fa-external-link" />
            </a>
          ) : null}
        </span>
      ),
    },
    {
      fieldName: "description",
      label: "Description",
      customRow: (row) => (
        <span className="parts-description" title={row.description || ""}>
          {displayValue(row.description)}
        </span>
      ),
    },
    {
      fieldName: "details",
      label: "Details",
      width: 280,
      customRow: (row) => {
        const mount = mountTypeMeta(row.mounting_type);
        const datasheetUrl = datasheetHref(row.datasheet_url);
        return (
          <div className="parts-details">
            <PartsDetailRow label="Mount">
              <span
                className={classnames("parts-mount-value", {
                  "is-smt": mount.key === "smt",
                  "is-tht": mount.key === "tht",
                  "is-empty": !mount.key,
                })}
              >
                {mount.label}
              </span>
            </PartsDetailRow>
            <PartsDetailRow label="Package">
              {displayValue(row.package)}
            </PartsDetailRow>
            <PartsDetailRow label="Status">
              {displayValue(row.part_status)}
            </PartsDetailRow>
            <PartsDetailRow label="Datasheet">
              {datasheetUrl ? (
                <a
                  href={datasheetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="parts-datasheet-link"
                  title="Open datasheet"
                >
                  View
                  <i className="fa fa-external-link" />
                </a>
              ) : (
                "N/A"
              )}
            </PartsDetailRow>
            <PartsDetailRow label="COO">
              {displayValue(row.country_of_origin)}
            </PartsDetailRow>
          </div>
        );
      },
    },
    {
      fieldName: "digikey_enriched_at",
      label: "Last Refreshed",
      width: 130,
      customRow: (row) =>
        row.digikey_enriched_at ? (
          <span className="parts-refreshed">
            <Moment format="DD/MM/YYYY HH:mm">{row.digikey_enriched_at}</Moment>
          </span>
        ) : (
          "N/A"
        ),
    },
    {
      fieldName: "",
      label: "Actions",
      width: 96,
      align: "center",
      customRow: (row) => {
        const job = queueStatusByPartId[row.id];
        const isBusy = job && (job.status === "pending" || job.status === "syncing");
        return (
          <div className="d-flex justify-content-center align-items-center parts-actions">
            <button
              type="button"
              className={classnames("digikey-refresh-btn", {
                "is-spinning": isBusy,
              })}
              title="Refresh DigiKey details"
              aria-label="Refresh DigiKey details"
              disabled={isBusy}
              onClick={() => enqueue(row)}
            >
              <i
                className={classnames("fa fa-refresh", {
                  "fa-spin": isBusy,
                })}
              />
            </button>
            {canDelete ? (
              <Button
                size="sm"
                color="default"
                title="Delete part"
                onClick={() => showDeleteConfirm(row)}
                disabled={isBusy}
              >
                <span className="fa fa-trash text-danger" />
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <Layouts>
      <DeleteConfirmation
        show={deleteConfirm}
        confirm={deleteConfirmPopup}
      />
      <div className="p-20 parts-page">
        <div className="card">
          <div className="cardHeader parts-card-header">
            <h3>
              Parts
              <small className="d-block">
                Total - {totalRecords} DigiKey-enriched part
                {totalRecords === 1 ? "" : "s"}.
              </small>
            </h3>
            <div className="parts-toolbar">
              <div className="searchBox parts-search">
                <Input
                  type="text"
                  placeholder="Search MPN, manufacturer, DigiKey PN…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <Input
                type="select"
                className="parts-filter"
                value={mountingType}
                onChange={(e) => {
                  setMountingType(e.target.value);
                  setPageActive(1);
                }}
              >
                <option value="">All mount types</option>
                <option value="Through Hole">Through Hole</option>
                <option value="Surface Mount">Surface Mount</option>
              </Input>
              <Input
                type="select"
                className="parts-filter"
                value={partStatus}
                onChange={(e) => {
                  setPartStatus(e.target.value);
                  setPageActive(1);
                }}
              >
                <option value="">All statuses</option>
                <option value="Active">Active</option>
                <option value="Obsolete">Obsolete</option>
              </Input>
              <Input
                type="select"
                className="parts-filter"
                value={countryOfOrigin}
                onChange={(e) => {
                  setCountryOfOrigin(e.target.value);
                  setPageActive(1);
                }}
              >
                <option value="">All COO</option>
                <option value="missing">Missing COO (N/A)</option>
              </Input>
              <Input
                type="select"
                className="parts-filter"
                value={String(showEntry)}
                onChange={(e) => {
                  setShowEntry(Number(e.target.value));
                  setPageActive(1);
                }}
              >
                {PER_PAGE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </Input>
            </div>
          </div>
          <Datatable
            data={parts}
            columns={columns}
            pageActive={1}
            showEntry={parts.length || showEntry}
            searchText=""
            changeTableParams={() => {}}
            loading={isLoading}
          />
        </div>
        {totalRecords > showEntry && (
          <Paginations
            totalRecords={totalRecords}
            showEntry={showEntry}
            pageActive={pageActive}
            click={(page) => setPageActive(page)}
            prevClick={() => setPageActive(Math.max(1, pageActive - 1))}
            nextClick={() =>
              setPageActive(
                Math.min(
                  Math.ceil(totalRecords / showEntry) || 1,
                  pageActive + 1,
                ),
              )
            }
          />
        )}
      </div>
    </Layouts>
  );
};

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps, {
  getPartsCatalog,
  deletePart,
})(Parts);
